import React from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import StarIcon from '@mui/icons-material/Star';

import {
  AgentSelectorSettings,
  LauncherLeadingIconGlyph,
} from '../../../store/modules/merchant/types';
import { EllaPersonalityDraft } from '../ellaDraft';
import {
  agentSelectorFormControlSx,
  agentSelectorTextFieldSx,
} from '../agentSelectorThemeFieldStyles';
import { EllaAgentSelectorColorField } from './EllaAgentSelectorColorField';
import { MobileLauncherStarGlyph } from './MobileLauncherStarGlyph';

const MOBILE_ICON_OPTIONS: { value: LauncherLeadingIconGlyph; label: string }[] = [
  { value: 'sparkle', label: 'Sparkle' },
  { value: 'star', label: 'Star' },
];

/** Matches Ella SDK `MultiLauncher` collapsed pill (Figma Multi-avatar collapsed). */
const COLLAPSED_PILL_HEIGHT_PX = 83;
const COLLAPSED_PILL_PAD_LEFT_PX = 8;
const COLLAPSED_PILL_PAD_RIGHT_PX = 10;
const COLLAPSED_SPARK_SIZE_PX = 22;
const COLLAPSED_SPARK_GAP_PX = 12;
const COLLAPSED_AVATAR_SIZE_PX = 65;
const COLLAPSED_AVATAR_OVERLAP_PX = 21;
const MOBILE_LAUNCHER_GAP_PX = 8;
const MOBILE_LAUNCHER_ICON_SIZE_PX = 14;
const MOBILE_LAUNCHER_PAD_X_PX = 12;
const MOBILE_LAUNCHER_PAD_Y_PX = 6;
const MOBILE_LAUNCHER_FONT_SIZE_PX = 14;
const MOBILE_LAUNCHER_LINE_HEIGHT_PX = 18;
const MOBILE_LAUNCHER_MIN_HEIGHT_PX = 36;
const THEME_CARD_GAP_PX = 24;
const THEME_SETTINGS_GAP_PX = 16;
const SINGLE_AGENT_AVATAR_SIZE_PX = 70;
const SINGLE_AGENT_SPARKLE_SIZE_PX = 22;
const THEME_ICON_OPTIONS_GAP_PX = 8;
const THEME_FIELD_ROW_WIDTH_PX = 265;

const collapsedAvatarStackWidth = (count: number): number => {
  if (count <= 0) {
    return 0;
  }
  const step = COLLAPSED_AVATAR_SIZE_PX - COLLAPSED_AVATAR_OVERLAP_PX;
  return COLLAPSED_AVATAR_SIZE_PX + (count - 1) * step;
};

const collapsedPillWidth = (avatarCount: number, showLeadingIcon: boolean): number => {
  const sparkBlock = showLeadingIcon ? COLLAPSED_SPARK_SIZE_PX + COLLAPSED_SPARK_GAP_PX : 0;
  return (
    COLLAPSED_PILL_PAD_LEFT_PX +
    sparkBlock +
    collapsedAvatarStackWidth(avatarCount) +
    COLLAPSED_PILL_PAD_RIGHT_PX
  );
};

const avatarCropSx = {
  objectFit: 'cover' as const,
  objectPosition: 'center top',
};

type Props = {
  agentSelector: AgentSelectorSettings;
  personalities: EllaPersonalityDraft[];
  readOnly: boolean;
  /** Shown when at least one agent is live on the website. */
  showDesktopTheme?: boolean;
  /** Pill background color — only when multiple agents are live. */
  showDesktopSelectorBackgroundColor?: boolean;
  showMobileTheme?: boolean;
  onChange: (next: AgentSelectorSettings) => void;
};

const LeadingIconPreview = ({
  glyph,
  color,
  size = COLLAPSED_SPARK_SIZE_PX,
}: {
  glyph: LauncherLeadingIconGlyph;
  color: string;
  size?: number;
}): JSX.Element => {
  const Icon = glyph === 'star' ? StarIcon : AutoAwesomeIcon;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        flexShrink: 0,
      }}
    >
      <Icon sx={{ fontSize: size, color }} aria-hidden />
    </Box>
  );
};

const CollapsedLauncherPreview = ({
  agentSelector,
  personalities,
}: {
  agentSelector: AgentSelectorSettings;
  personalities: EllaPersonalityDraft[];
}): JSX.Element => {
  const visible = personalities.filter(
    (personality) => personality.enabled && personality.avatarSrc,
  );
  const stackWidth = collapsedAvatarStackWidth(visible.length);
  const pillWidth = collapsedPillWidth(visible.length, agentSelector.showLeadingIcon);

  return (
    <Box
      data-testid="ella-agent-selector-preview"
      sx={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        boxSizing: 'border-box',
        width: pillWidth,
        minWidth: pillWidth,
        height: COLLAPSED_PILL_HEIGHT_PX,
        pl: `${COLLAPSED_PILL_PAD_LEFT_PX}px`,
        pr: `${COLLAPSED_PILL_PAD_RIGHT_PX}px`,
        borderRadius: '9999px',
        bgcolor: agentSelector.backgroundColor,
        overflow: 'hidden',
        flexShrink: 0,
        maxWidth: '100%',
      }}
    >
      {agentSelector.showLeadingIcon ? (
        <LeadingIconPreview
          glyph={agentSelector.leadingIcon}
          color={agentSelector.leadingIconColor}
        />
      ) : null}
      {agentSelector.showLeadingIcon && visible.length > 0 ? (
        <Box sx={{ width: COLLAPSED_SPARK_GAP_PX, flexShrink: 0 }} aria-hidden />
      ) : null}
      <Box
        sx={{
          position: 'relative',
          width: stackWidth,
          height: COLLAPSED_AVATAR_SIZE_PX,
          flexShrink: 0,
          ml: agentSelector.showLeadingIcon ? 0 : 'auto',
        }}
      >
        {visible.map((personality, index) => (
          <Box
            key={personality.clientId}
            sx={{
              position: 'absolute',
              top: 0,
              left: index * (COLLAPSED_AVATAR_SIZE_PX - COLLAPSED_AVATAR_OVERLAP_PX),
              boxSizing: 'border-box',
              width: COLLAPSED_AVATAR_SIZE_PX,
              height: COLLAPSED_AVATAR_SIZE_PX,
              borderRadius: '50%',
              bgcolor: '#FEFEFE',
              border: '1px solid #000',
              overflow: 'hidden',
              zIndex: visible.length - index,
            }}
          >
            <Box
              component="img"
              src={personality.avatarSrc}
              alt=""
              sx={{
                width: '100%',
                height: '100%',
                display: 'block',
                ...avatarCropSx,
              }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

/** Single live agent — matches Ella SDK `ImageAvatar` idle launcher (Figma 1270:1071). */
const SingleAgentLauncherPreview = ({
  agentSelector,
  personalities,
}: {
  agentSelector: AgentSelectorSettings;
  personalities: EllaPersonalityDraft[];
}): JSX.Element | null => {
  const liveAgent = personalities.find(
    (personality) => personality.enabled && personality.avatarSrc,
  );
  if (!liveAgent?.avatarSrc) {
    return null;
  }

  return (
    <Box
      data-testid="ella-agent-selector-single-preview"
      sx={{
        position: 'relative',
        width: SINGLE_AGENT_AVATAR_SIZE_PX,
        height: SINGLE_AGENT_AVATAR_SIZE_PX,
        flexShrink: 0,
      }}
    >
      {agentSelector.showLeadingIcon ? (
        <Box
          sx={{
            position: 'absolute',
            top: -4,
            left: -4,
            zIndex: 1,
            pointerEvents: 'none',
          }}
        >
          <LeadingIconPreview
            glyph={agentSelector.leadingIcon}
            color={agentSelector.leadingIconColor}
            size={SINGLE_AGENT_SPARKLE_SIZE_PX}
          />
        </Box>
      ) : null}
      <Box
        sx={{
          width: SINGLE_AGENT_AVATAR_SIZE_PX,
          height: SINGLE_AGENT_AVATAR_SIZE_PX,
          borderRadius: '50%',
          overflow: 'hidden',
          boxShadow: '0 1px 8px 0 rgba(0, 0, 0, 0.65)',
          bgcolor: '#FEFEFE',
        }}
      >
        <Box
          component="img"
          src={liveAgent.avatarSrc}
          alt=""
          sx={{
            width: '100%',
            height: '100%',
            display: 'block',
            ...avatarCropSx,
          }}
        />
      </Box>
    </Box>
  );
};

const MobileLauncherPreview = ({
  agentSelector,
}: {
  agentSelector: AgentSelectorSettings;
}): JSX.Element => (
  <Box
    data-testid="ella-agent-selector-mobile-preview"
    sx={{
      display: 'inline-flex',
      boxSizing: 'border-box',
      minHeight: `${MOBILE_LAUNCHER_MIN_HEIGHT_PX}px`,
      padding: `${MOBILE_LAUNCHER_PAD_Y_PX}px ${MOBILE_LAUNCHER_PAD_X_PX}px`,
      justifyContent: 'center',
      alignItems: 'center',
      gap: `${MOBILE_LAUNCHER_GAP_PX}px`,
      width: 'max-content',
      maxWidth: '100%',
      alignSelf: 'flex-start',
      borderRadius: '999px',
      background: `color-mix(in srgb, ${agentSelector.mobile.backgroundColor} 85%, transparent)`,
      fontFamily: 'Poppins, sans-serif',
      fontSize: MOBILE_LAUNCHER_FONT_SIZE_PX,
      fontWeight: 600,
      lineHeight: `${MOBILE_LAUNCHER_LINE_HEIGHT_PX}px`,
      whiteSpace: 'nowrap',
      flexShrink: 0,
    }}
  >
    {agentSelector.mobile.showLeadingIcon ? (
      agentSelector.mobile.leadingIcon === 'star' ? (
        <MobileLauncherStarGlyph
          size={MOBILE_LAUNCHER_ICON_SIZE_PX}
          color={agentSelector.mobile.leadingIconColor}
        />
      ) : (
        <LeadingIconPreview
          glyph="sparkle"
          color={agentSelector.mobile.leadingIconColor}
          size={MOBILE_LAUNCHER_ICON_SIZE_PX}
        />
      )
    ) : null}
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        lineHeight: 0,
        color: agentSelector.mobile.labelTextColor,
      }}
    >
      <Box
        component="span"
        sx={{
          m: 0,
          p: 0,
          fontFamily: 'inherit',
          fontSize: MOBILE_LAUNCHER_FONT_SIZE_PX,
          fontWeight: 600,
          lineHeight: `${MOBILE_LAUNCHER_LINE_HEIGHT_PX}px`,
        }}
      >
        {agentSelector.mobile.labelText}
      </Box>
    </Box>
  </Box>
);

const themeCardSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: '16px',
  width: '100%',
  maxWidth: THEME_FIELD_ROW_WIDTH_PX,
  p: 2,
  borderRadius: '12px',
  border: '1px solid #E0E0E0',
  bgcolor: '#FFFFFF',
  boxSizing: 'border-box',
} as const;

const themeCardHeadingSx = {
  fontFamily: 'Poppins, sans-serif',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.6px',
  lineHeight: '16px',
  color: '#757575',
  textTransform: 'uppercase',
} as const;

const themeFieldRowSx = {
  width: '100%',
} as const;

const themeSettingsStackSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: `${THEME_SETTINGS_GAP_PX}px`,
  width: '100%',
} as const;

const themePreviewRowSx = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
} as const;

const themeMobilePreviewRowSx = {
  ...themePreviewRowSx,
  justifyContent: 'flex-end',
} as const;

export const AgentSelectorThemeSection = ({
  agentSelector,
  personalities,
  readOnly,
  showDesktopTheme = true,
  showDesktopSelectorBackgroundColor = true,
  showMobileTheme = true,
  onChange,
}: Props): JSX.Element => {
  const patch = (partial: Partial<AgentSelectorSettings>) =>
    onChange({ ...agentSelector, ...partial });

  const patchMobile = (partial: Partial<AgentSelectorSettings['mobile']>) =>
    onChange({ ...agentSelector, mobile: { ...agentSelector.mobile, ...partial } });

  const isSingleLiveAgentDesktop = !showDesktopSelectorBackgroundColor;

  return (
    <Box
      data-testid="ella-agent-selector-theme"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: `${THEME_CARD_GAP_PX}px`,
        width: '100%',
      }}
    >
      {showDesktopTheme ? (
        <Box sx={themeCardSx} data-testid="ella-agent-selector-desktop-theme">
          <Typography sx={themeCardHeadingSx}>Agent selector theme & preview</Typography>

          <Box sx={themePreviewRowSx}>
            {isSingleLiveAgentDesktop ? (
              <SingleAgentLauncherPreview
                agentSelector={agentSelector}
                personalities={personalities}
              />
            ) : (
              <CollapsedLauncherPreview
                agentSelector={agentSelector}
                personalities={personalities}
              />
            )}
          </Box>

          <Box sx={themeSettingsStackSx}>
            {showDesktopSelectorBackgroundColor ? (
              <EllaAgentSelectorColorField
                label="Background color"
                value={agentSelector.backgroundColor}
                disabled={readOnly}
                dataTestId="ella-agent-selector-pill-bg"
                onChange={(backgroundColor) => patch({ backgroundColor })}
              />
            ) : null}
            <FormControlLabel
              sx={{
                m: 0,
                ml: 0,
                mr: 0,
                alignItems: 'center',
                gap: `${THEME_ICON_OPTIONS_GAP_PX}px`,
                '& .MuiFormControlLabel-label': {
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: 12,
                  lineHeight: '16px',
                  color: '#1E1E1E',
                },
              }}
              control={
                <Checkbox
                  checked={agentSelector.showLeadingIcon}
                  disabled={readOnly}
                  onChange={(event) => patch({ showLeadingIcon: event.target.checked })}
                  inputProps={{ 'aria-label': 'Display star icon with avatar(s)' }}
                  data-testid="ella-agent-selector-show-icon"
                  sx={{
                    p: 0,
                    m: 0,
                    width: 24,
                    height: 24,
                    '& .MuiSvgIcon-root': { fontSize: 24 },
                  }}
                />
              }
              label="Display star icon with avatar(s)"
            />
            {agentSelector.showLeadingIcon ? (
              <Box sx={themeFieldRowSx}>
                <EllaAgentSelectorColorField
                  label="Star color"
                  value={agentSelector.leadingIconColor}
                  disabled={readOnly}
                  dataTestId="ella-agent-selector-icon-color"
                  onChange={(leadingIconColor) => patch({ leadingIconColor })}
                />
              </Box>
            ) : null}
          </Box>
        </Box>
      ) : null}

      {showMobileTheme ? (
        <Box
          sx={themeCardSx}
          data-testid="ella-agent-selector-mobile-theme"
          role="group"
          aria-labelledby="ella-agent-selector-mobile-theme-heading"
        >
          <Typography id="ella-agent-selector-mobile-theme-heading" sx={themeCardHeadingSx}>
            Mobile launcher theme & preview
          </Typography>

          <Box sx={themeMobilePreviewRowSx}>
            <MobileLauncherPreview agentSelector={agentSelector} />
          </Box>

          <Box sx={themeSettingsStackSx}>
            <EllaAgentSelectorColorField
              label="Background color"
              value={agentSelector.mobile.backgroundColor}
              disabled={readOnly}
              dataTestId="ella-agent-selector-mobile-bg"
              onChange={(backgroundColor) => patchMobile({ backgroundColor })}
            />
            <TextField
              label="Label text"
              value={agentSelector.mobile.labelText}
              disabled={readOnly}
              onChange={(event) => patchMobile({ labelText: event.target.value })}
              InputLabelProps={{ shrink: true }}
              inputProps={{ maxLength: 80 }}
              error={!agentSelector.mobile.labelText.trim()}
              helperText={!agentSelector.mobile.labelText.trim() ? 'Enter label text.' : undefined}
              data-testid="ella-agent-selector-mobile-label"
              fullWidth
              sx={agentSelectorTextFieldSx}
            />
            <EllaAgentSelectorColorField
              label="Label text color"
              value={agentSelector.mobile.labelTextColor}
              disabled={readOnly}
              dataTestId="ella-agent-selector-mobile-label-color"
              onChange={(labelTextColor) => patchMobile({ labelTextColor })}
            />
            <FormControlLabel
              sx={{
                m: 0,
                ml: 0,
                mr: 0,
                alignItems: 'center',
                gap: `${THEME_ICON_OPTIONS_GAP_PX}px`,
                '& .MuiFormControlLabel-label': {
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: 12,
                  lineHeight: '16px',
                  color: '#1E1E1E',
                },
              }}
              control={
                <Checkbox
                  checked={agentSelector.mobile.showLeadingIcon}
                  disabled={readOnly}
                  onChange={(event) => patchMobile({ showLeadingIcon: event.target.checked })}
                  inputProps={{ 'aria-label': 'Display leading icon on mobile launcher' }}
                  data-testid="ella-agent-selector-mobile-show-icon"
                  sx={{
                    p: 0,
                    m: 0,
                    width: 24,
                    height: 24,
                    '& .MuiSvgIcon-root': { fontSize: 24 },
                  }}
                />
              }
              label="Display leading icon"
            />
            {agentSelector.mobile.showLeadingIcon ? (
              <>
                <FormControl fullWidth disabled={readOnly} sx={agentSelectorFormControlSx}>
                  <InputLabel id="ella-agent-selector-mobile-icon-label" shrink>
                    Leading icon
                  </InputLabel>
                  <Select
                    labelId="ella-agent-selector-mobile-icon-label"
                    label="Leading icon"
                    notched
                    value={agentSelector.mobile.leadingIcon}
                    onChange={(event: SelectChangeEvent) =>
                      patchMobile({ leadingIcon: event.target.value as LauncherLeadingIconGlyph })
                    }
                    data-testid="ella-agent-selector-mobile-icon"
                  >
                    {MOBILE_ICON_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <EllaAgentSelectorColorField
                  label="Leading icon color"
                  value={agentSelector.mobile.leadingIconColor}
                  disabled={readOnly}
                  dataTestId="ella-agent-selector-mobile-icon-color"
                  onChange={(leadingIconColor) => patchMobile({ leadingIconColor })}
                />
              </>
            ) : null}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};
